import { useEffect, useState } from "react";
import { ttsUrl } from "@/lib/api";

/**
 * Walk a lesson's activities and collect every unique short text string that
 * is likely to be spoken by the TTS engine. We keep this conservative — just
 * enough phrases to keep a student playing when WiFi drops mid-drill.
 */
function collectPhrases(lesson) {
  const seen = new Set();
  const push = (s) => {
    if (typeof s !== "string") return;
    const t = s.trim();
    if (!t || t.length > 180) return;
    seen.add(t);
  };
  if (!lesson) return [];
  // Mirror the backend allow-list (allowed_tts_from) so every request is useful.
  (lesson.tutorial?.steps || []).forEach((s) => {
    push(s.title); push(s.body); push(s.term); push(s.definition);
    (s.bullets || []).forEach(push);
  });
  (lesson.activities || []).forEach((a) => {
    push(a.prompt);
    const c = a.config || {};
    push(c.question); push(c.word);
    (c.options || []).forEach((o) => push(String(o)));
    (c.sounds || []).forEach((s) => push(s?.say));
    (c.pairs || []).forEach((p) => { push(p?.left); push(p?.right); });
  });
  return [...seen];
}

/**
 * Preload the TTS audio for every phrase in a lesson. Fires plain `fetch()`
 * calls to `/api/tts?...` in parallel (throttled) so the service worker
 * caches them under `zoodleap-tts`. Requests are `no-store` when the SW is
 * absent so we don't fight the HTTP cache in dev.
 */
export function preloadLesson(lesson, voice = "nova", { concurrency = 2, onProgress } = {}) {
  const phrases = collectPhrases(lesson);
  const urls = phrases.map((p) => ttsUrl(p, voice));
  if (urls.length === 0) return Promise.resolve({ total: 0, done: 0 });
  let done = 0;
  const results = { total: urls.length, done: 0, failed: 0 };
  const queue = [...urls];

  const worker = async () => {
    while (queue.length) {
      const u = queue.shift();
      try {
        await fetch(u, { credentials: "omit", cache: "default" });
      } catch { results.failed += 1; }
      done += 1;
      results.done = done;
      if (onProgress) onProgress(results);
    }
  };
  return Promise.all(Array.from({ length: Math.min(concurrency, urls.length) }, worker))
    .then(() => results);
}

/**
 * React hook for the lesson page. Preloads the current lesson's TTS on mount
 * and reports progress so the UI can render an "Offline ready" badge.
 */
export function useLessonPreload(lesson, voice = "nova") {
  const [state, setState] = useState({ ready: false, total: 0, done: 0, running: false });
  useEffect(() => {
    if (!lesson || !lesson.activities) return;
    if (!("serviceWorker" in navigator)) return; // no caching path — skip
    let cancelled = false;
    setState({ ready: false, total: 0, done: 0, running: true });
    // Give the first narration a head start: the preloader would otherwise
    // compete with it for the browser's per-host connection limit.
    const timer = setTimeout(() => {
      if (cancelled) return;
      preloadLesson(lesson, voice, {
        onProgress: (r) => { if (!cancelled) setState({ ...r, ready: false, running: true }); },
      })
        .then((r) => { if (!cancelled) setState({ ...r, ready: r.done > 0 && r.failed === 0, running: false }); })
        .catch(() => { if (!cancelled) setState((s) => ({ ...s, running: false })); });
    }, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [lesson?.id, voice]);   // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/**
 * Sends `navigator.onLine` changes as booleans. Component-friendly wrapper.
 */
export function useOnlineStatus() {
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}
